import React, { useState } from 'react';
import { LogOut, ShoppingCart, Settings, Download, Upload, Receipt, User as UserIcon, AlertCircle, Activity, Coins } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

interface AuthDropdownProps {
  user: any;
  totalCredits: number;
  getTextColor: (p: number) => string;
  setShowTopUp: (show: boolean) => void;
  setShowMenu: (show: boolean) => void;
  setShowSettings: (show: boolean) => void;
  setShowExport: (show: boolean) => void;
  setShowImport: (show: boolean) => void;
  setShowTransactions: (show: boolean) => void;
  setShowUsages: (show: boolean) => void;
  signOut: () => void;
}

export const AuthDropdown = ({
  user,
  totalCredits,
  getTextColor,
  setShowTopUp,
  setShowMenu,
  setShowSettings,
  setShowExport,
  setShowImport,
  setShowTransactions,
  setShowUsages,
  signOut
}: AuthDropdownProps) => {
  const { sendVerificationEmail } = useAuth();
  const [isSending, setIsSending] = useState(false);

  const handleVerify = async () => {
    setIsSending(true);
    try {
      await sendVerificationEmail();
      toast.success('Verification email sent! Please check your inbox.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to send verification email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSelection = (action: () => void) => {
    setShowMenu(false);
    setTimeout(() => {
      action();
    }, 150);
  };

  return (
    <>
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 cursor-default">
        <div className="flex items-center gap-3">
          {user.photoURL ? (
            <img src={user.photoURL} loading="lazy" alt="Avatar" className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-200 dark:border-slate-800 pointer-events-none" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400">
              <UserIcon className="w-5 h-5 pointer-events-none" />
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <p className="text-sm pb-0.5 font-medium text-slate-900 dark:text-white truncate">{user.displayName || 'User'}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
            {!user?.emailVerified && (
              <button
                onClick={handleVerify}
                disabled={isSending}
                className="text-left mt-1 text-[11px] font-medium text-amber-600 hover:text-amber-700 dark:text-amber-500 dark:hover:text-amber-400 transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-50"
              >
                <AlertCircle className="w-3 h-3" />
                {isSending ? 'Sending...' : 'Verify now'}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 py-2 sm:hidden border-t border-slate-100 dark:border-slate-800 mt-3">
          {!user?.emailVerified ? (
            <div className="text-amber-500 text-xs font-medium text-left">Pending Verification</div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs font-medium mb-1">
                <span className="text-slate-600 dark:text-slate-400 gap-1 flex items-center">
                  <Coins className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" /> Remaining Credits
                </span>
                <span className={getTextColor(totalCredits)}>
                  {totalCredits % 1 === 0 ? totalCredits : totalCredits.toFixed(1)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      
      <button
        onClick={() => handleSelection(() => setShowTopUp(true))}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          <span>Buy Credits</span>
        </div>
      </button>

      <button
        onClick={() => handleSelection(() => setShowExport(true))}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <Download className="w-4 h-4" />
        Export Data
      </button>

      <button
        onClick={() => handleSelection(() => setShowImport(true))}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <Upload className="w-4 h-4" />
        Import Data
      </button>

      <button
        onClick={() => handleSelection(() => setShowTransactions(true))}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <Receipt className="w-4 h-4" />
        Transaction History
      </button>

      <button
        onClick={() => handleSelection(() => setShowUsages(true))}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <Coins className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        Usage History
      </button>

      <button
        onClick={() => handleSelection(() => setShowSettings(true))}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
      >
        <Settings className="w-4 h-4" />
        Settings
      </button>

      <button
        onClick={() => handleSelection(() => signOut())}
        className="w-full flex items-center gap-2 px-4 py-2.5 mt-1 text-sm text-red-600 border-t border-slate-100 dark:border-slate-800 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-left cursor-pointer"
      >
        <LogOut className="w-4 h-4" />
        Sign Out
      </button>
    </>
  );
};
